import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, Pencil, RefreshCcw, Trash2 } from "lucide-react";

const MAX = 4, MIN = 1;
const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

// Full-screen photo viewer: swipe (or arrows/dots) between an item's Reference
// and Proof shots, and pinch / double-tap to zoom in for close inspection.
export function PhotoLightbox({ data, onClose, onAnnotate, onReplace, onDelete, testId = "lightbox" }) {
  const photos = data?.photos || [];
  const [i, setI] = useState(data?.index || 0);
  const [t, setT] = useState({ s: 1, x: 0, y: 0 });
  const [anim, setAnim] = useState(false);
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const ptrs = useRef(new Map());
  const g = useRef(null);
  const lastTap = useRef(0);

  useEffect(() => { setI(data?.index || 0); setAnim(false); setT({ s: 1, x: 0, y: 0 }); }, [data]);

  const multi = photos.length > 1;
  const cur = photos[i];
  const hasActions = !!(onAnnotate || onReplace || onDelete) && !!cur?.photo;

  const resetView = () => { setAnim(true); setT({ s: 1, x: 0, y: 0 }); };
  const goto = (n) => { setI(clamp(n, 0, photos.length - 1)); setAnim(true); setT({ s: 1, x: 0, y: 0 }); };

  const clampPan = (s, x, y) => {
    const el = imgRef.current, cont = containerRef.current;
    if (!el || !cont) return { x, y };
    const overX = Math.max(0, (el.offsetWidth * s - cont.clientWidth) / 2);
    const overY = Math.max(0, (el.offsetHeight * s - cont.clientHeight) / 2);
    return { x: clamp(x, -overX, overX), y: clamp(y, -overY, overY) };
  };

  const zoomTo = (s2, focalX, focalY) => {
    const cont = containerRef.current; if (!cont) return;
    const rect = cont.getBoundingClientRect();
    const cx = rect.left + rect.width / 2, cy = rect.top + rect.height / 2;
    setT((prev) => {
      const fx = (focalX - cx - prev.x) / prev.s;
      const fy = (focalY - cy - prev.y) / prev.s;
      const c = clampPan(s2, focalX - cx - fx * s2, focalY - cy - fy * s2);
      return { s: s2, x: c.x, y: c.y };
    });
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") goto(i + 1);
      else if (e.key === "ArrowLeft") goto(i - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }); // eslint-disable-line react-hooks/exhaustive-deps

  const onPointerDown = (e) => {
    ptrs.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    setAnim(false);
    if (ptrs.current.size === 2) {
      const [a, b] = [...ptrs.current.values()];
      g.current = { mode: "pinch", startDist: Math.hypot(a.x - b.x, a.y - b.y), startS: t.s, startX: t.x, startY: t.y, midX: (a.x + b.x) / 2, midY: (a.y + b.y) / 2 };
    } else {
      g.current = { mode: "single", startX: e.clientX, startY: e.clientY, baseX: t.x, baseY: t.y, moved: 0, t0: Date.now() };
    }
  };

  const onPointerMove = (e) => {
    if (!ptrs.current.has(e.pointerId)) return;
    ptrs.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const gc = g.current; if (!gc) return;
    if (gc.mode === "pinch" && ptrs.current.size >= 2) {
      const [a, b] = [...ptrs.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const midX = (a.x + b.x) / 2, midY = (a.y + b.y) / 2;
      const s2 = clamp(gc.startS * (dist / gc.startDist), MIN, MAX);
      const cont = containerRef.current; const rect = cont.getBoundingClientRect();
      const cx = rect.left + rect.width / 2, cy = rect.top + rect.height / 2;
      const fx = (gc.midX - cx - gc.startX) / gc.startS;
      const fy = (gc.midY - cy - gc.startY) / gc.startS;
      const c = clampPan(s2, midX - cx - fx * s2, midY - cy - fy * s2);
      setT({ s: s2, x: c.x, y: c.y });
    } else if (gc.mode === "single") {
      const dx = e.clientX - gc.startX, dy = e.clientY - gc.startY;
      gc.moved = Math.max(gc.moved, Math.hypot(dx, dy));
      if (t.s > 1) {
        const c = clampPan(t.s, gc.baseX + dx, gc.baseY + dy);
        setT((p) => ({ ...p, x: c.x, y: c.y }));
      } else if (multi && Math.abs(dx) > Math.abs(dy)) {
        setT({ s: 1, x: dx * 0.5, y: 0 }); // elastic swipe feedback
      }
    }
  };

  const onPointerUp = (e) => {
    const gc = g.current;
    ptrs.current.delete(e.pointerId);
    if (gc && gc.mode === "single") {
      const dx = e.clientX - gc.startX, dy = e.clientY - gc.startY;
      const dt = Date.now() - gc.t0;
      if (gc.moved < 10 && dt < 300) {
        const now = Date.now();
        if (now - lastTap.current < 300) {
          lastTap.current = 0;
          if (t.s > 1) resetView(); else { setAnim(true); zoomTo(2.5, e.clientX, e.clientY); }
        } else {
          lastTap.current = now;
        }
      } else if (t.s === 1 && multi && Math.abs(dx) > 70 && Math.abs(dx) > Math.abs(dy)) {
        goto(dx < 0 ? i + 1 : i - 1);
      } else if (t.s <= 1.02) {
        resetView();
      }
    }
    if (gc && gc.mode === "pinch" && ptrs.current.size < 2) {
      if (t.s <= 1.05) resetView();
    }
    if (ptrs.current.size === 1) {
      const [p] = [...ptrs.current.values()];
      g.current = { mode: "single", startX: p.x, startY: p.y, baseX: t.x, baseY: t.y, moved: 0, t0: Date.now() };
    } else if (ptrs.current.size === 0) {
      g.current = null;
    }
  };

  if (!photos.length) return null;

  return createPortal(
    <div className="fixed inset-0 z-[70] select-none bg-black/95" data-testid={testId}>
      <button
        className="absolute right-4 z-30 text-white"
        style={{ top: "calc(env(safe-area-inset-top) + 1rem)" }}
        onClick={onClose}
        data-testid={`${testId}-close`}
      >
        <X className="h-7 w-7" />
      </button>

      <div className="absolute left-1/2 z-30 flex -translate-x-1/2 items-center gap-2" style={{ top: "calc(env(safe-area-inset-top) + 1.15rem)" }}>
        <span className={`rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide ${cur.label === "Proof" ? "bg-emerald-500/25 text-emerald-300" : "bg-white/15 text-white/80"}`} data-testid="lightbox-label">{cur.label}</span>
        {multi && <span className="text-xs font-semibold text-white/50">{i + 1} / {photos.length}</span>}
      </div>

      <div
        ref={containerRef}
        className="absolute inset-0 flex items-center justify-center overflow-hidden"
        style={{ touchAction: "none" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <img
          ref={imgRef}
          key={cur.url}
          src={cur.url}
          alt={cur.label}
          draggable={false}
          data-testid="lightbox-image"
          className="max-h-[82dvh] max-w-full rounded-lg object-contain"
          style={{
            transform: `translate(${t.x}px, ${t.y}px) scale(${t.s})`,
            transformOrigin: "center center",
            transition: anim ? "transform 0.22s ease-out" : "none",
            willChange: "transform",
          }}
        />
      </div>

      {multi && t.s === 1 && (
        <>
          <button className={`absolute left-3 top-1/2 z-30 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur ${i === 0 ? "pointer-events-none opacity-30" : ""}`} onClick={() => goto(i - 1)} data-testid="lightbox-prev"><ChevronLeft className="h-6 w-6" /></button>
          <button className={`absolute right-3 top-1/2 z-30 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur ${i === photos.length - 1 ? "pointer-events-none opacity-30" : ""}`} onClick={() => goto(i + 1)} data-testid="lightbox-next"><ChevronRight className="h-6 w-6" /></button>
          <div className="absolute left-1/2 flex -translate-x-1/2 gap-2" style={{ bottom: hasActions ? "calc(env(safe-area-inset-bottom) + 5.5rem)" : "calc(env(safe-area-inset-bottom) + 2rem)" }}>
            {photos.map((_, n) => (
              <button key={n} onClick={() => goto(n)} className={`h-2 rounded-full transition-all ${n === i ? "w-6 bg-emerald-400" : "w-2 bg-white/40"}`} aria-label={`Go to photo ${n + 1}`} />
            ))}
          </div>
        </>
      )}

      {t.s === 1 && !hasActions && (
        <div className="pointer-events-none absolute left-1/2 z-30 -translate-x-1/2 text-[11px] font-medium text-white/40" style={{ bottom: "calc(env(safe-area-inset-bottom) + 0.75rem)" }}>
          Pinch or double-tap to zoom
        </div>
      )}

      {hasActions && t.s === 1 && (
        <div className="absolute left-1/2 z-30 flex -translate-x-1/2 items-center gap-2.5" style={{ bottom: "calc(env(safe-area-inset-bottom) + 1.25rem)" }}>
          {onAnnotate && <LightboxAct icon={Pencil} label="Annotate" testId={`${testId}-annotate`} onClick={() => { const c = photos[i]; onClose(); onAnnotate(c.itemId, c.photo); }} />}
          {onReplace && <LightboxAct icon={RefreshCcw} label="Replace" testId={`${testId}-replace`} onClick={() => { const c = photos[i]; onClose(); onReplace(c.itemId, c.photo); }} />}
          {onDelete && <LightboxAct icon={Trash2} label="Delete" danger testId={`${testId}-delete`} onClick={() => { const c = photos[i]; onDelete(c.itemId, c.photo.id); onClose(); }} />}
        </div>
      )}
    </div>,
    document.body,
  );
}

function LightboxAct({ icon: Icon, label, onClick, testId, danger }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-white backdrop-blur transition-transform active:scale-95 ${danger ? "bg-rose-600/90 hover:bg-rose-600" : "bg-white/15 hover:bg-white/25"}`}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}
